import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import DisclaimerHeader from './components/DisclaimerHeader';
import TermsContent from './components/TermsContent';
import AcceptanceSection from './components/AcceptanceSection';

const LegalDisclaimerTermsAgreement = () => {
  const navigate = useNavigate();
  const termsRef = useRef(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [acceptances, setAcceptances] = useState({
    fundLoss: false,
    noSupport: false,
    noLiability: false,
    generalTerms: false
  });
  const [typedAgreement, setTypedAgreement] = useState('');
  const [loading, setLoading] = useState(false);

  const requiredPhrase = 'I AGREE';
  const allAccepted = Object.values(acceptances)?.every(val => val);
  const phraseMatches = typedAgreement?.toUpperCase() === requiredPhrase;
  const canProceed = allAccepted && phraseMatches && hasScrolledToBottom;

  useEffect(() => {
    const handleScroll = () => {
      const element = termsRef?.current;
      if (element) {
        const scrolledToBottom = 
          element?.scrollHeight - element?.scrollTop <= element?.clientHeight + 50;
        if (scrolledToBottom && !hasScrolledToBottom) {
          setHasScrolledToBottom(true);
        }
      }
    };

    const element = termsRef?.current;
    element?.addEventListener('scroll', handleScroll);
    return () => element?.removeEventListener('scroll', handleScroll);
  }, [hasScrolledToBottom]);

  const handleAcceptanceChange = (key) => {
    setAcceptances(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleAccept = async () => {
    setLoading(true);
    
    // Simulate acceptance recording (IP logging, timestamp, etc.)
    const acceptanceRecord = {
      timestamp: new Date()?.toISOString(),
      ipAddress: 'Logged',
      userAgent: navigator.userAgent,
      termsVersion: '1.0.0',
      acceptances: acceptances
    };
    
    // Store acceptance in localStorage (in production, send to backend)
    localStorage.setItem('legal_acceptance', JSON.stringify(acceptanceRecord));
    
    setTimeout(() => {
      setLoading(false);
      navigate('/signup');
    }, 1500);
  };

  const handleDecline = () => {
    navigate('/signin');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <div className="bg-card rounded-lg shadow-lg border border-border overflow-hidden">
          <DisclaimerHeader />

          <div 
            ref={termsRef}
            className="p-6 max-h-[500px] overflow-y-auto border-b border-border bg-muted/30"
          >
            <TermsContent />
            
            {!hasScrolledToBottom && (
              <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-t from-muted/90 to-transparent pt-8 pb-4 text-center">
                <div className="flex items-center justify-center gap-2 text-muted-foreground animate-bounce">
                  <Icon name="ArrowDown" size={20} />
                  <span className="text-sm font-medium">Scroll to read all terms</span>
                </div>
              </div>
            )}
          </div>

          <AcceptanceSection
            acceptances={acceptances}
            onAcceptanceChange={handleAcceptanceChange}
            typedAgreement={typedAgreement}
            onTypedAgreementChange={setTypedAgreement}
            requiredPhrase={requiredPhrase}
            hasScrolledToBottom={hasScrolledToBottom}
          />

          <div className="p-6 bg-muted/20 border-t border-border">
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleDecline}
                iconName="X"
                disabled={loading}
              >
                Decline & Exit
              </Button>
              <Button
                onClick={handleAccept}
                disabled={!canProceed || loading}
                loading={loading}
                iconName="CheckCircle"
              >
                {loading ? 'Processing...' : 'Accept & Continue'}
              </Button>
            </div>
            
            {!canProceed && hasScrolledToBottom && (
              <p className="text-xs text-muted-foreground text-center mt-3">
                Complete all acknowledgments and type the agreement phrase to proceed
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">
            Document Version 1.0.0 | Effective Date: January 18, 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalDisclaimerTermsAgreement;